// ---------------------------------------------------------------------------
// O HANDLE — o que vem antes de .alfredo.food.
//
// UMA lista de reservados, exportada daqui e consumida pelos três repositórios.
// No Raio existem DUAS listas, uma no middleware (7 nomes) e outra no backend
// (12), e elas divergiram: um nome reservado só no backend é aceito na gravação
// pelo middleware e depois nunca resolve. Aqui a lista é uma só, por construção.
//
// Regras do formato, e o porquê de cada uma:
//  · minúsculas, dígitos e hífen  — é o que sobrevive a ser DITADO no telefone
//    e a ser impresso num cardápio
//  · começa e termina com letra ou dígito — hífen na ponta é erro de digitação
//  · 3 a 40 caracteres — dois caracteres colidem com sigla; 40 já não cabe
//  · sem hífen duplo — `xn--` é prefixo de punycode e confunde navegador
// ---------------------------------------------------------------------------

export const HANDLE_PADRAO = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

/**
 * Nomes que não podem virar handle de restaurante.
 *
 * Três motivos: subdomínio que já é nosso (www, api, admin), palavra que o
 * cliente leria como sendo do Alfredo (pedidos, central, ops) e nome que
 * qualquer serviço espera poder usar (mail, ftp, cdn).
 *
 * `portal` entrou em 29/08/2026 e é de um quarto tipo: ele é um subdomínio QUE
 * JÁ ESTÁ EM USO — `portal.alfredo.food` é o endereço da área do cliente
 * (`src/comum/hostDeMarca.ts`).
 *
 * O RISCO É EXATAMENTE ESTE, e não o que se escreveu primeiro aqui: sem a
 * linha, o Central deixaria um restaurante REGISTRAR o handle `portal`, e ele
 * ficaria com um endereço que nunca abre a landing dele — impresso no cardápio
 * e no QR, e sempre respondendo a tela de conta de outra pessoa. O que a linha
 * NÃO impede é o sequestro do portal, porque isso já está impedido em outro
 * lugar: `handleDoHost()` devolve `null` para `portal.alfredo.food` numa guarda
 * anterior à lista, comentada lá como redundante DE PROPÓSITO. Descrever o
 * risco maior do que ele é foi o que fez esta reserva parecer opcional e parar
 * na cópia errada — ver abaixo.
 *
 * ONDE EDITAR — e este parágrafo é escrito por FUNÇÃO, nunca por posição, pelo
 * motivo que ele mesmo descreve: o arquivo existe em três repositórios
 * (`alfredo-central-backend`, `alfredo-central-web`, `alfredo-pedidos`) e viaja
 * byte a byte entre eles. Um texto que diga "esta cópia" fica verdadeiro em um
 * destino e falso nos outros dois — foi o que aconteceu com a primeira versão
 * deste comentário, apanhada em revisão em 29/08/2026.
 *
 * A cópia CANÔNICA é a do `alfredo-central-backend`, porque é ela que GRAVA o
 * handle (`handle.service.ts` → `normalizarHandle`). As outras duas só LEEM.
 * A trava do `prebuild` compara o sha256 do diretório com o `ASSINATURA.json`
 * LOCAL — os dois andam juntos em cada repositório, então uma cópia que fique
 * para trás passa VERDE e a divergência é silenciosa. Editar numa cópia que só
 * lê deixaria a reserva inerte onde importa, e a próxima
 * `node ferramentas/sincronizarSite.js` rodada a partir da canônica apagaria a
 * mudança sem barulho. O caminho é: editar na canônica, sincronizar as três,
 * conferir que `verificarSite.js` passa nas três, e commitar as três.
 */
export const HANDLES_RESERVADOS: string[] = [
  "admin",
  "alfredo",
  "api",
  "app",
  "assets",
  "blog",
  "cdn",
  "central",
  "checkout",
  "cliente",
  "conta",
  "dev",
  "docs",
  "email",
  "ftp",
  "help",
  "homolog",
  "img",
  "login",
  "mail",
  "ops",
  "painel",
  "pedido",
  "pedidos",
  "portal",
  "preview",
  "root",
  "rota",
  "sac",
  "seguranca",
  "sistema",
  "staging",
  "static",
  "status",
  "suporte",
  "teste",
  "webmail",
  "www",
];

const RESERVADOS = new Set(HANDLES_RESERVADOS);

export function handleReservado(handle: string): boolean {
  return RESERVADOS.has(handle);
}

/**
 * Normaliza e valida. Lança com mensagem pronta para a tela — quem troca o
 * endereço público do próprio restaurante precisa entender o que deu errado.
 */
export function normalizarHandle(bruto: unknown): string {
  if (typeof bruto !== "string") {
    throw new Error("O endereço do site precisa ser um texto.");
  }

  const handle = bruto.trim().toLowerCase();

  if (!handle) {
    throw new Error("O endereço do site não pode ficar vazio.");
  }
  if (handle.length < 3 || handle.length > 40) {
    throw new Error("O endereço do site precisa ter de 3 a 40 caracteres.");
  }
  if (!HANDLE_PADRAO.test(handle)) {
    throw new Error(
      "O endereço do site aceita apenas letras minúsculas, números e hífen, " +
        "e não pode começar nem terminar com hífen.",
    );
  }
  if (handle.includes("--")) {
    throw new Error("O endereço do site não pode ter dois hífens seguidos.");
  }
  if (handleReservado(handle)) {
    throw new Error(`"${handle}" é um endereço reservado do sistema. Escolha outro.`);
  }

  return handle;
}

/** Sugestão a partir do nome da marca. É o que a tela preenche sozinha. */
export function sugerirHandle(nome: string): string {
  const base = nome
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 40);
  return base.length >= 3 ? base : "";
}

/** O endereço completo, como o dono vai ler na tela e imprimir no cardápio. */
export const DOMINIO_DO_SITE = "alfredo.food";

export function enderecoPublico(handle: string): string {
  return `${handle}.${DOMINIO_DO_SITE}`;
}
